import { EMPTY_SCHEDULE, type Shift, scheduleText } from "@orbis/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";
import { ArrowRightIcon, CheckIcon, SparkleIcon } from "../../components/icons";
import { useToast } from "../../components/ui";
import { api } from "../../lib/api";
import { keys, useCompany, useLumiConfig, useMe } from "../../lib/queries";
import type { BusinessKind, CompanyProfile, LumiConfig, SetupProposal } from "../../lib/types";
import { AuthCheck, AuthFrame, AuthSteps } from "../auth/AuthShell";
import { Choice, TextArea } from "./LumiPage";
import { DAYS, Shifts, valid, WEEKDAYS } from "./ScheduleCard";
import { welcomeSkipKey } from "./welcome";

interface Answers {
  kind: BusinessKind;
  description: string;
  topics: string;
  address: LumiConfig["address"];
  tone: LumiConfig["tone"];
  pricePolicy: LumiConfig["pricePolicy"];
  customWeDo: string;
  customWeDont: string;
  customNeedToKnow: string;
  collectFields: LumiConfig["collectFields"];
  collectMode: LumiConfig["collectMode"];
  greeting: string;
  phone: string;
  email: string;
  companyAddress: string;
  /** Hours from the quick setup; untouched keeps whatever the company had. */
  hoursTouched: boolean;
  /** The hours the company already had, in words (null: none). */
  savedHours: string | null;
  alwaysOpen: boolean;
  hoursDays: number[];
  hoursShifts: Shift[];
}

type Step =
  | "describe"
  | "company"
  | "hours"
  | "kind"
  | "about"
  | "address"
  | "prices"
  | "custom"
  | "data"
  | "summary"
  | "done";

/** Steps as the brand side lists them. */
const STEP_LABEL: Record<Step, string> = {
  describe: "Tu negocio",
  company: "Contacto",
  hours: "Horario",
  kind: "Qué vende",
  about: "Qué hace",
  address: "Cómo habla",
  prices: "Precios",
  custom: "A la medida",
  data: "Datos del cliente",
  summary: "Resumen",
  done: "Listo",
};

const KIND_LABEL: Record<BusinessKind, string> = {
  products: "Vendo productos",
  services: "Doy servicios con precio fijo",
  custom: "Hago trabajos a la medida",
  mixed: "Vendo productos y hago trabajos a la medida",
};

const OFFER: Record<BusinessKind, LumiConfig["offerType"]> = {
  products: "catalog",
  services: "catalog",
  custom: "custom",
  mixed: "mixed",
};

const FIELDS = [
  { value: "name", label: "Nombre" },
  { value: "location", label: "Provincia y cantón" },
  { value: "customer_type", label: "Si compra para la casa, un negocio o una institución" },
  { value: "email", label: "Correo" },
] as const;

/** The steps for this kind of business: made-to-order work adds what it does and needs. */
const stepsFor = (kind: BusinessKind): Step[] => [
  "describe",
  "company",
  "hours",
  "kind",
  "about",
  "address",
  "prices",
  ...(kind === "custom" || kind === "mixed" ? (["custom"] as const) : []),
  "data",
  "summary",
  "done",
];

const fromSaved = (c: LumiConfig, company: CompanyProfile): Answers => ({
  phone: company.phone ?? "",
  email: company.email ?? "",
  companyAddress: company.address ?? "",
  // A company without hours sees a starting week and saves it; one with hours keeps them unless it changes them.
  hoursTouched: !company.schedule,
  savedHours: company.schedule ? company.hours : null,
  alwaysOpen: company.schedule?.alwaysOpen ?? false,
  hoursDays: WEEKDAYS,
  hoursShifts: [{ from: "08:00", to: "17:00" }],
  kind: c.offerType === "custom" ? "custom" : c.offerType === "mixed" ? "mixed" : "products",
  description: c.businessDescription ?? "",
  topics: c.topics ?? "",
  address: c.address,
  tone: c.tone,
  pricePolicy: c.pricePolicy,
  customWeDo: c.customWeDo ?? "",
  customWeDont: c.customWeDont ?? "",
  customNeedToKnow: c.customNeedToKnow ?? "",
  collectFields: c.collectFields.length ? c.collectFields : ["name", "location"],
  collectMode: c.collectMode === "off" ? "natural" : c.collectMode,
  greeting: c.greeting ?? "",
});

/**
 * The guided setup (f4-11): five or six questions, one per screen, with options that
 * depend on the kind of business. "Descríbeme tu negocio" can fill them in from a
 * paragraph; the person reviews every answer before it is saved.
 */
export function SetupWizard() {
  const config = useLumiConfig();
  const company = useCompany();
  if (!config.data || !company.data)
    return <div className="auth-shell">{config.isError || company.isError ? "No se pudo cargar." : "Cargando…"}</div>;
  return <Wizard initial={fromSaved(config.data, company.data)} />;
}

function Wizard({ initial }: { initial: Answers }) {
  const [a, setA] = useState<Answers>(initial);
  const [at, setAt] = useState(0);
  const [dir, setDir] = useState(1);
  const [paragraph, setParagraph] = useState("");
  const [note, setNote] = useState<string | null>(null);
  const toast = useToast();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const company = useCompanyName();
  const me = useMe().data;
  const tenantId = (me?.tenants.find((t) => t.tenantId === me.activeTenantId) ?? me?.tenants[0])?.tenantId;
  const steps = stepsFor(a.kind);
  const step = steps[Math.min(at, steps.length - 1)] as Step;
  const set = <K extends keyof Answers>(k: K, v: Answers[K]) => setA((x) => ({ ...x, [k]: v }));
  const go = (delta: number) => {
    setDir(delta);
    setAt((i) => Math.max(0, Math.min(steps.length - 1, i + delta)));
  };

  const propose = useMutation({
    mutationFn: (text: string) =>
      api.post<{ status: "ok"; proposal: SetupProposal } | { status: "daily_limit" | "failed" }>("/ai/setup/propose", {
        text,
      }),
    onSuccess: (r) => {
      if (r.status !== "ok") {
        setNote(
          r.status === "daily_limit"
            ? "Ya se usaron las propuestas de hoy. Responde las preguntas una por una."
            : "No se pudo armar la propuesta. Responde las preguntas una por una.",
        );
        return;
      }
      const p = r.proposal;
      setA((x) => ({
        ...x,
        kind: p.businessKind ?? x.kind,
        description: p.description ?? x.description,
        topics: p.topics ?? x.topics,
        address: p.treatment ?? x.address,
        tone: p.tone ?? x.tone,
        pricePolicy: p.pricePolicy ?? x.pricePolicy,
        customWeDo: p.customWeDo ?? x.customWeDo,
        customWeDont: p.customWeDont ?? x.customWeDont,
        customNeedToKnow: p.customNeedToKnow ?? x.customNeedToKnow,
        greeting: p.greeting ?? x.greeting,
        phone: p.phone ?? x.phone,
        email: p.email ?? x.email,
        companyAddress: p.address ?? x.companyAddress,
      }));
      toast("Propuesta lista: revisa cada respuesta");
      go(1);
    },
    onError: () => setNote("No se pudo armar la propuesta. Responde las preguntas una por una."),
  });

  const save = useMutation({
    mutationFn: async () => {
      const offerType = OFFER[a.kind];
      await api.put("/company", {
        phone: a.phone,
        email: a.email,
        address: a.companyAddress,
        ...(a.hoursTouched
          ? {
              schedule: {
                ...EMPTY_SCHEDULE,
                alwaysOpen: a.alwaysOpen,
                week: EMPTY_SCHEDULE.week.map((_, day) => (a.hoursDays.includes(day) ? a.hoursShifts : [])),
              },
            }
          : {}),
      });
      await api.put("/ai/config", {
        offerType,
        businessDescription: a.description,
        topics: a.topics,
        address: a.address,
        tone: a.tone,
        pricePolicy: a.pricePolicy,
        ...(offerType !== "catalog"
          ? { customWeDo: a.customWeDo, customWeDont: a.customWeDont, customNeedToKnow: a.customNeedToKnow }
          : {}),
        collectFields: a.collectMode === "off" ? [] : a.collectFields,
        collectMode: a.collectMode,
        greeting: a.greeting,
      });
      await api.post("/ai/setup/done");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.ai });
      qc.invalidateQueries({ queryKey: keys.company });
      go(1);
    },
    onError: () => toast("No se pudo guardar. Intenta de nuevo."),
  });

  const skip = () => {
    try {
      if (tenantId) localStorage.setItem(welcomeSkipKey(tenantId), "1");
    } catch {
      // Storage blocked: the tour may show again on the next visit.
    }
    navigate({ to: "/bandeja" });
  };

  const canNext =
    step === "hours"
      ? !a.hoursTouched || a.alwaysOpen || (a.hoursDays.length > 0 && a.hoursShifts.length > 0 && valid(a.hoursShifts))
      : step === "company"
        ? !a.email.trim() || /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(a.email.trim())
        : step === "about"
          ? a.description.trim().length >= 10
          : step === "data"
            ? a.collectMode === "off" || a.collectFields.length > 0
            : true;

  return (
    <AuthFrame
      brand={{
        side: "left",
        title: step === "done" ? "¡Bienvenido a Orbis!" : "Configuremos Orbis",
        text:
          step === "done"
            ? "Lumi ya conoce tu negocio. Ahora súmale tu catálogo y tus manuales."
            : "Unos minutos y Lumi atiende tu WhatsApp como alguien de tu equipo.",
        extra: (
          <AuthSteps
            steps={steps.filter((x) => x !== "done").map((x) => STEP_LABEL[x])}
            current={step === "done" ? steps.length : at}
          />
        ),
        action:
          step !== "done" ? (
            <button className="auth-link welcome-skip" type="button" onClick={skip}>
              Lo hago después
            </button>
          ) : undefined,
      }}
    >
      <div className="welcome-pane">
        <div>
          <p className="welcome-count">{step === "done" ? "Listo" : `Paso ${at + 1} de ${steps.length - 1}`}</p>
          <AnimatePresence mode="wait" custom={dir} initial={false}>
            <motion.div
              key={step}
              className="setup-step"
              custom={dir}
              initial={{ opacity: 0, x: dir * 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: dir * -24 }}
              transition={{ duration: 0.18, ease: [0.23, 1, 0.32, 1] }}
            >
              {step === "describe" && (
                <>
                  <h2>Descríbeme tu negocio</h2>
                  <p className="muted">
                    Escribe en un párrafo qué hace {company}, para quién y cómo atienden. Lumi propone la configuración
                    y tú revisas cada respuesta.
                  </p>
                  <TextArea
                    id="setup-paragraph"
                    label="Tu negocio en tus palabras"
                    max={3000}
                    rows={6}
                    value={paragraph}
                    onChange={setParagraph}
                    placeholder="Ej.: Somos una distribuidora de electrodomésticos y plantillas de gas en Alajuela. Vendemos a hogares y restaurantes de todo el país; los precios los da un asesor. Atendemos de usted."
                  />
                  {note && <p className="error-text">{note}</p>}
                  {/* One way forward: with a paragraph Lumi proposes the answers; without one, the questions follow. */}
                  {paragraph.trim() ? (
                    <button
                      className="auth-submit"
                      type="button"
                      disabled={paragraph.trim().length < 20 || propose.isPending}
                      aria-busy={propose.isPending}
                      onClick={() => {
                        setNote(null);
                        propose.mutate(paragraph);
                      }}
                    >
                      {propose.isPending ? <span className="auth-spinner" aria-hidden="true" /> : <SparkleIcon />}
                      {propose.isPending
                        ? "Pensando…"
                        : paragraph.trim().length < 20
                          ? "Escribe un poco más"
                          : "Proponer configuración"}
                    </button>
                  ) : (
                    <button className="auth-submit" type="button" onClick={() => go(1)}>
                      Siguiente
                      <ArrowRightIcon />
                    </button>
                  )}
                  <p className="faint welcome-hint">
                    Si prefieres, déjalo en blanco y responde las preguntas una por una.
                  </p>
                </>
              )}
              {step === "company" && (
                <>
                  <h2>¿Cómo los contactan?</h2>
                  <p className="muted">
                    Lumi se lo da a los clientes que lo pidan. Puedes dejar en blanco lo que no tengas.
                  </p>
                  <div className="field">
                    <label htmlFor="welcome-phone">Teléfono</label>
                    <input
                      className="input"
                      id="welcome-phone"
                      type="tel"
                      maxLength={40}
                      placeholder="Ej.: 2440 1234"
                      value={a.phone}
                      onChange={(e) => set("phone", e.target.value)}
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="welcome-email">Correo</label>
                    <input
                      className="input"
                      id="welcome-email"
                      type="email"
                      maxLength={200}
                      placeholder="ventas@empresa.com"
                      value={a.email}
                      onChange={(e) => set("email", e.target.value)}
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="welcome-address">Dirección</label>
                    <input
                      className="input"
                      id="welcome-address"
                      maxLength={300}
                      placeholder="Ej.: 200 m oeste del Parque Central, Alajuela"
                      value={a.companyAddress}
                      onChange={(e) => set("companyAddress", e.target.value)}
                    />
                  </div>
                </>
              )}
              {step === "hours" && (
                <>
                  <h2>¿Cuándo atienden?</h2>
                  <p className="muted">
                    Fuera de horario, Lumi avisa cuándo responde un asesor. Los feriados y los cambios se ajustan
                    después en Empresa.
                  </p>
                  {a.hoursTouched ? (
                    <>
                      <AuthCheck checked={a.alwaysOpen} onChange={(v) => set("alwaysOpen", v)}>
                        Abierto las 24 horas, todos los días
                      </AuthCheck>
                      {!a.alwaysOpen && (
                        <>
                          <div className="day-chips">
                            {DAYS.map((d) => (
                              <button
                                key={d.day}
                                type="button"
                                aria-pressed={a.hoursDays.includes(d.day)}
                                aria-label={d.label}
                                title={d.label}
                                onClick={() =>
                                  set(
                                    "hoursDays",
                                    a.hoursDays.includes(d.day)
                                      ? a.hoursDays.filter((y) => y !== d.day)
                                      : [...a.hoursDays, d.day],
                                  )
                                }
                              >
                                {d.short}
                              </button>
                            ))}
                          </div>
                          <Shifts
                            shifts={a.hoursShifts}
                            label="Horario"
                            emptyLabel="Agregar turno"
                            onChange={(next) => set("hoursShifts", next)}
                          />
                        </>
                      )}
                      <button className="auth-link" type="button" onClick={() => set("hoursTouched", false)}>
                        {a.savedHours ? "Dejar el horario que ya tenía" : "Lo pongo después"}
                      </button>
                    </>
                  ) : (
                    <>
                      <p>{a.savedHours ? `Horario actual: ${a.savedHours}.` : "Sin horario por ahora."}</p>
                      <div>
                        <button
                          className="auth-ghost auth-ghost-sm"
                          type="button"
                          onClick={() => set("hoursTouched", true)}
                        >
                          {a.savedHours ? "Cambiar el horario" : "Poner el horario"}
                        </button>
                      </div>
                    </>
                  )}
                </>
              )}
              {step === "done" && (
                <div className="welcome-done">
                  <span className="welcome-check" aria-hidden="true">
                    <CheckIcon />
                  </span>
                  <h2>¡Listo! Lumi ya conoce {company}</h2>
                  <p className="muted">
                    Lo siguiente hace que responda mejor. Lumi sigue apagada hasta que la pruebes y la enciendas.
                  </p>
                  <Link className="auth-submit" to="/lumi">
                    Probar Lumi
                    <ArrowRightIcon />
                  </Link>
                  <div className="welcome-next">
                    <Link className="auth-ghost" to="/catalogo">
                      Subir el catálogo
                    </Link>
                    <Link className="auth-ghost" to="/conocimiento">
                      Agregar garantías o manuales
                    </Link>
                  </div>
                  <Link className="auth-link" to="/bandeja">
                    Ir a la bandeja
                  </Link>
                </div>
              )}
              {step === "kind" && (
                <>
                  <h2>¿Qué vende la empresa?</h2>
                  <Choice
                    name="setup-kind"
                    label="Elige la que más se parece"
                    value={a.kind}
                    onChange={(v) => set("kind", v)}
                    options={[
                      { value: "products", label: KIND_LABEL.products, hint: "Tienda o distribuidora" },
                      { value: "services", label: KIND_LABEL.services, hint: "Por ejemplo, mantenimiento o clases" },
                      { value: "custom", label: KIND_LABEL.custom, hint: "Cada trabajo se cotiza aparte" },
                      { value: "mixed", label: KIND_LABEL.mixed, hint: "Las dos cosas" },
                    ]}
                  />
                </>
              )}
              {step === "about" && (
                <>
                  <h2>¿Qué hace la empresa y para quién?</h2>
                  <p className="muted">Lumi lo usa para presentarse y saber de qué puede hablar.</p>
                  <TextArea
                    id="setup-description"
                    label="Qué hace la empresa"
                    max={2000}
                    rows={4}
                    value={a.description}
                    onChange={(v) => set("description", v)}
                    placeholder="Ej.: Distribuidora de electrodomésticos y plantillas de gas para hogares y restaurantes en todo Costa Rica."
                  />
                  <TextArea
                    id="setup-topics"
                    label="Temas en los que ayuda (opcional)"
                    max={2000}
                    rows={3}
                    value={a.topics}
                    onChange={(v) => set("topics", v)}
                    placeholder={"Uno por línea. Ej.:\nProductos y garantías\nDónde comprar"}
                  />
                </>
              )}
              {step === "address" && (
                <>
                  <h2>¿Cómo le habla a tus clientes?</h2>
                  <Choice
                    name="setup-address"
                    label="Trato"
                    value={a.address}
                    onChange={(v) => set("address", v)}
                    options={[
                      { value: "usted", label: "Usted", hint: "¿En qué le puedo ayudar?" },
                      { value: "tu", label: "Tú", hint: "¿En qué te puedo ayudar?" },
                      { value: "vos", label: "Vos", hint: "¿Qué andás buscando?" },
                    ]}
                  />
                  <Choice
                    name="setup-tone"
                    label="Tono"
                    value={a.tone}
                    onChange={(v) => set("tone", v)}
                    options={[
                      { value: "cercano", label: "Cercano", hint: "Amable y natural" },
                      { value: "formal", label: "Formal", hint: "Respetuoso y sobrio" },
                    ]}
                  />
                </>
              )}
              {step === "prices" && (
                <>
                  <h2>¿Lumi da precios?</h2>
                  <Choice
                    name="setup-prices"
                    label="Precios"
                    value={a.pricePolicy}
                    onChange={(v) => set("pricePolicy", v)}
                    options={
                      a.kind === "custom"
                        ? [
                            { value: "no_share", label: "No, se cotizan", hint: "Un asesor da el precio" },
                            { value: "public", label: "Sí, los que tengo fijos", hint: "Del catálogo" },
                          ]
                        : [
                            { value: "no_share", label: "No", hint: "Un asesor da el precio" },
                            { value: "public", label: "Sí, los del catálogo", hint: "Precios fijos" },
                            {
                              value: "by_customer_type",
                              label: "Según el cliente",
                              hint: "Casa, negocio o institución",
                            },
                          ]
                    }
                  />
                  <p className="faint">Lumi nunca inventa un precio: solo da los que estén en el catálogo.</p>
                </>
              )}
              {step === "custom" && (
                <>
                  <h2>Sobre los trabajos a la medida</h2>
                  <p className="muted">
                    Lumi pregunta lo necesario y pasa el requerimiento a un asesor, sin prometer precio ni plazo.
                  </p>
                  <TextArea
                    id="setup-we-do"
                    label="Lo que hacemos"
                    max={2000}
                    rows={3}
                    value={a.customWeDo}
                    onChange={(v) => set("customWeDo", v)}
                    placeholder="Ej.: Muebles de cocina y closets a la medida, con instalación en el GAM."
                  />
                  <TextArea
                    id="setup-we-dont"
                    label="Lo que no hacemos"
                    max={2000}
                    rows={2}
                    value={a.customWeDont}
                    onChange={(v) => set("customWeDont", v)}
                    placeholder="Ej.: Reparaciones de muebles que no fabricamos."
                  />
                  <TextArea
                    id="setup-need"
                    label="Lo que necesitamos saber para cotizar"
                    max={2000}
                    rows={3}
                    value={a.customNeedToKnow}
                    onChange={(v) => set("customNeedToKnow", v)}
                    placeholder={"Uno por línea. Ej.:\nMedidas\nMaterial y color\nFecha deseada"}
                  />
                </>
              )}
              {step === "data" && (
                <>
                  <h2>¿Qué datos le pide al cliente?</h2>
                  <p className="muted">Los guarda en la ficha del contacto, sin preguntar lo que el cliente ya dijo.</p>
                  <Choice
                    name="setup-collect"
                    label="Cuándo"
                    value={a.collectMode}
                    onChange={(v) => set("collectMode", v)}
                    options={[
                      { value: "first", label: "En la primera respuesta", hint: "Responde y pide los datos" },
                      { value: "natural", label: "Cuando venga al caso", hint: "Recomendado" },
                      { value: "off", label: "No pedir datos" },
                    ]}
                  />
                  {a.collectMode !== "off" && (
                    <fieldset className="choice">
                      <legend>Qué datos</legend>
                      <div className="lumi-checks">
                        {FIELDS.map((f) => (
                          <AuthCheck
                            key={f.value}
                            checked={a.collectFields.includes(f.value)}
                            onChange={() =>
                              set(
                                "collectFields",
                                a.collectFields.includes(f.value)
                                  ? a.collectFields.filter((x) => x !== f.value)
                                  : [...a.collectFields, f.value],
                              )
                            }
                          >
                            {f.label}
                          </AuthCheck>
                        ))}
                      </div>
                    </fieldset>
                  )}
                  <div className="field">
                    <label htmlFor="setup-greeting">Saludo de la primera respuesta (opcional)</label>
                    <input
                      className="input"
                      id="setup-greeting"
                      maxLength={300}
                      placeholder={`Ej.: ¡Hola! Gracias por escribir a ${company}.`}
                      value={a.greeting}
                      onChange={(e) => set("greeting", e.target.value)}
                    />
                  </div>
                </>
              )}
              {step === "summary" && (
                <>
                  <h2>Listo, revisa la configuración</h2>
                  <dl className="setup-summary">
                    {summaryRows(a).map((row) => (
                      <div key={row.label} className="setup-summary-row">
                        <dt>{row.label}</dt>
                        <dd>{row.value || <span className="faint">Sin completar</span>}</dd>
                        <button
                          className="auth-link setup-summary-edit"
                          type="button"
                          aria-label={`Cambiar ${row.label.toLowerCase()}`}
                          onClick={() => {
                            setDir(-1);
                            setAt(steps.indexOf(row.step));
                          }}
                        >
                          Cambiar
                        </button>
                      </div>
                    ))}
                  </dl>
                  <p className="faint">
                    Todo se puede cambiar después en Lumi y en Empresa. Pruébala antes de encenderla.
                  </p>
                </>
              )}
            </motion.div>
          </AnimatePresence>
          {step !== "describe" && step !== "done" && (
            <div className="welcome-nav">
              <button className="auth-ghost" type="button" onClick={() => go(-1)}>
                Atrás
              </button>
              {step === "summary" ? (
                <button
                  className="auth-submit"
                  type="button"
                  disabled={save.isPending}
                  aria-busy={save.isPending}
                  onClick={() => save.mutate()}
                >
                  {save.isPending && <span className="auth-spinner" aria-hidden="true" />}
                  {save.isPending ? "Guardando…" : "Guardar"}
                </button>
              ) : (
                <button className="auth-submit" type="button" disabled={!canNext} onClick={() => go(1)}>
                  Siguiente
                  <ArrowRightIcon />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </AuthFrame>
  );
}

/** Every answer, in the order of the steps, with the step to go back to for each. */
function summaryRows(a: Answers): { label: string; value: string; step: Step }[] {
  const hours = a.hoursTouched
    ? scheduleText({
        ...EMPTY_SCHEDULE,
        alwaysOpen: a.alwaysOpen,
        week: EMPTY_SCHEDULE.week.map((_, day) => (a.hoursDays.includes(day) ? a.hoursShifts : [])),
      })
    : (a.savedHours ?? "");
  const contact = [a.phone, a.email, a.companyAddress]
    .map((x) => x.trim())
    .filter(Boolean)
    .join(" · ");
  const madeToOrder = a.kind === "custom" || a.kind === "mixed";
  const data =
    a.collectMode === "off"
      ? "No pide datos"
      : `${FIELDS.filter((f) => a.collectFields.includes(f.value))
          .map((f) => f.label.toLowerCase())
          .join(", ")} · ${a.collectMode === "first" ? "en la primera respuesta" : "cuando venga al caso"}`;
  return [
    { label: "Contacto", value: contact, step: "company" },
    { label: "Horario", value: hours, step: "hours" },
    { label: "Negocio", value: KIND_LABEL[a.kind], step: "kind" },
    { label: "Qué hace", value: a.description.trim(), step: "about" },
    { label: "Temas", value: a.topics.trim().split("\n").filter(Boolean).join(" · "), step: "about" },
    {
      label: "Trato",
      value: `${{ usted: "Usted", tu: "Tú", vos: "Vos" }[a.address]}, ${a.tone}`,
      step: "address",
    },
    {
      label: "Precios",
      value: {
        no_share: "No los da: pasa a un asesor",
        public: "Da los del catálogo",
        by_customer_type: "Según el tipo de cliente",
      }[a.pricePolicy],
      step: "prices",
    },
    ...(madeToOrder
      ? ([
          { label: "Lo que hacemos", value: a.customWeDo.trim(), step: "custom" },
          { label: "Lo que no hacemos", value: a.customWeDont.trim(), step: "custom" },
          {
            label: "Para cotizar",
            value: a.customNeedToKnow.trim().split("\n").filter(Boolean).join(" · "),
            step: "custom",
          },
        ] as const)
      : []),
    { label: "Datos del cliente", value: data, step: "data" },
    { label: "Saludo", value: a.greeting.trim(), step: "data" },
  ];
}

function useCompanyName() {
  const me = useMe().data;
  const active = me?.tenants.find((t) => t.tenantId === me.activeTenantId) ?? me?.tenants[0];
  return active?.tenantName ?? "la empresa";
}
